/**
 * Brand overview for the storefront theme (section brand-list.liquid).
 *
 * Reads page 1 of the brand JSON inlined by the section, fetches any further pages from the
 * brands-json view (stores with more than 250 brands), then renders Top brands, an A–Z + "0-9"
 * index, category chips and a name filter — all client-side, no reloads.
 *
 * No dependencies, no build step: dropped into the theme's assets/ folder as-is. The pure helpers
 * are also exported for the app repo's tests (module.exports branch below), which is the only
 * automated check this file gets.
 */
;(function (root, factory) {
  var api = factory()
  if (typeof module === 'object' && module.exports) {
    module.exports = api
  } else {
    root.BrandList = api
    if (typeof document !== 'undefined') {
      var start = function () {
        document.querySelectorAll('[data-brand-list]').forEach(api.mount)
      }
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start)
      else start()
    }
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict'

  var OTHER = '0-9'
  var LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')
  var SEARCH_ICON =
    '<svg class="brand-list__search-icon" aria-hidden="true" focusable="false" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="11" cy="11" r="7"/><path d="m20 20-4-4"/></svg>'

  // "Avène" → A, "Öko" → O, "ß…" → S, "Dr. Böhm" → D (as on shop-apotheke.at), "3M" → 0-9.
  function initialLetter(title) {
    var base = String(title || '')
      .trim()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/ß/g, 's')
    var first = base.charAt(0).toUpperCase()
    return /[A-Z]/.test(first) ? first : OTHER
  }

  function compareTitles(a, b) {
    return String(a.title || '').localeCompare(String(b.title || ''), 'de', { sensitivity: 'base' })
  }

  function sortBrands(brands) {
    return brands.slice().sort(compareTitles)
  }

  // Every letter of the index, in display order, each with its (possibly empty) brand list.
  function groupByLetter(brands) {
    var groups = {}
    LETTERS.concat(OTHER).forEach(function (letter) {
      groups[letter] = []
    })
    sortBrands(brands).forEach(function (brand) {
      groups[initialLetter(brand.title)].push(brand)
    })
    return LETTERS.concat(OTHER).map(function (letter) {
      return { letter: letter, brands: groups[letter] }
    })
  }

  function filterBrands(brands, options) {
    var query = String((options && options.query) || '')
      .trim()
      .toLowerCase()
    var category = options && options.category
    return brands.filter(function (brand) {
      var matchesQuery = !query || String(brand.title || '').toLowerCase().indexOf(query) !== -1
      var matchesCategory = !category || (brand.categories || []).indexOf(category) !== -1
      return matchesQuery && matchesCategory
    })
  }

  function categoriesOf(brands) {
    var seen = {}
    brands.forEach(function (brand) {
      ;(brand.categories || []).forEach(function (category) {
        seen[category] = true
      })
    })
    return Object.keys(seen).sort(function (a, b) {
      return a.localeCompare(b, 'de', { sensitivity: 'base' })
    })
  }

  // Page 1 comes inline; pages 2..N are fetched in parallel and appended in page order.
  function loadAllBrands(firstPage, fetchPage) {
    var pages = (firstPage.meta && firstPage.meta.pages) || 1
    var requests = []
    for (var page = 2; page <= pages; page++) requests.push(fetchPage(page))
    return Promise.all(requests).then(function (rest) {
      return rest.reduce(
        function (all, data) {
          return all.concat(data.brands || [])
        },
        (firstPage.brands || []).slice()
      )
    })
  }

  function el(tag, className, text) {
    var node = document.createElement(tag)
    if (className) node.className = className
    if (text !== undefined) node.textContent = text
    return node
  }

  function brandLink(brand, withLogo) {
    var node = el(brand.url ? 'a' : 'span', 'brand-list__brand' + (withLogo ? ' brand-list__brand--logo' : ''))
    if (brand.url) node.href = brand.url
    if (withLogo && brand.image_url) {
      var media = el('span', 'brand-list__brand-media')
      var img = el('img')
      img.src = brand.image_url
      img.alt = brand.title
      img.loading = 'lazy'
      media.appendChild(img)
      node.appendChild(media)
    }
    node.appendChild(el('span', 'brand-list__brand-name' + (withLogo ? ' body-small' : ''), brand.title))
    return node
  }

  // Category sidebar: "All" plus every category, each with the number of brands matching the
  // current search. Rendered as a vertical list on desktop, a horizontal scroller on mobile (CSS).
  function renderSidebar(brands, categories, state, labels, rerender) {
    var sidebar = el('aside', 'brand-list__sidebar')
    sidebar.setAttribute('aria-label', labels.categories)
    sidebar.appendChild(el('h2', 'brand-list__sidebar-heading', labels.categories))

    var searched = filterBrands(brands, { query: state.query })
    var list = el('ul', 'brand-list__categories')
    ;[''].concat(categories).forEach(function (category) {
      var active = state.category === category
      var count = category ? filterBrands(searched, { category: category }).length : searched.length
      var item = el('li', 'brand-list__category-item')
      var button = el('button', 'brand-list__category' + (active ? ' is-active' : ''))
      button.type = 'button'
      button.setAttribute('aria-pressed', active ? 'true' : 'false')
      button.appendChild(el('span', 'brand-list__category-name', category || labels.all))
      button.appendChild(el('span', 'brand-list__category-count', '(' + count + ')'))
      button.addEventListener('click', function () {
        state.category = category
        rerender()
      })
      item.appendChild(button)
      list.appendChild(item)
    })
    sidebar.appendChild(list)
    return sidebar
  }

  function render(container, brands, state, labels, rerender) {
    container.innerHTML = ''

    var categories = categoriesOf(brands)
    var layout = el('div', 'brand-list__layout' + (categories.length ? ' brand-list__layout--sidebar' : ''))
    if (categories.length) layout.appendChild(renderSidebar(brands, categories, state, labels, rerender))
    var main = el('div', 'brand-list__main')
    layout.appendChild(main)
    container.appendChild(layout)

    var featured = sortBrands(
      brands.filter(function (brand) {
        return brand.featured
      })
    )
    if (featured.length && !state.query && !state.category) {
      var top = el('section', 'brand-list__top')
      top.appendChild(el('h2', 'brand-list__top-heading heading-medium', labels.top))
      var grid = el('div', 'brand-list__top-grid')
      featured.forEach(function (brand) {
        grid.appendChild(brandLink(brand, true))
      })
      top.appendChild(grid)
      main.appendChild(top)
    }

    var controls = el('div', 'brand-list__controls')
    var searchField = el('label', 'brand-list__search-field')
    searchField.innerHTML = SEARCH_ICON
    var search = el('input', 'brand-list__search')
    search.type = 'search'
    search.placeholder = labels.search
    search.setAttribute('aria-label', labels.search)
    search.autocomplete = 'off'
    search.value = state.query
    searchField.appendChild(search)
    search.addEventListener('input', function () {
      state.query = search.value
      rerender(true)
    })
    controls.appendChild(searchField)
    main.appendChild(controls)

    var groups = groupByLetter(filterBrands(brands, state))
    var index = el('nav', 'brand-list__index')
    index.setAttribute('aria-label', 'A–Z')
    groups.forEach(function (group) {
      var link = el(group.brands.length ? 'a' : 'span', 'brand-list__index-letter body-small', group.letter)
      if (!group.brands.length) link.setAttribute('aria-disabled', 'true')
      if (group.brands.length) link.href = '#brands-' + group.letter
      index.appendChild(link)
    })
    main.appendChild(index)

    var nonEmpty = groups.filter(function (group) {
      return group.brands.length
    })
    if (!nonEmpty.length) main.appendChild(el('p', 'brand-list__empty body-medium', labels.empty))
    nonEmpty.forEach(function (group) {
      var section = el('section', 'brand-list__group')
      section.id = 'brands-' + group.letter
      section.appendChild(el('h2', 'brand-list__group-letter heading-small', group.letter))
      var list = el('ul', 'brand-list__group-brands')
      group.brands.forEach(function (brand) {
        var item = el('li', 'brand-list__group-item')
        item.appendChild(brandLink(brand, false))
        list.appendChild(item)
      })
      section.appendChild(list)
      main.appendChild(section)
    })
  }

  function mount(root) {
    var dataNode = root.querySelector('[data-brand-list-data]')
    var container = root.querySelector('[data-brand-list-app]')
    if (!dataNode || !container) return

    var labels = {
      all: root.dataset.labelAll || 'Alle',
      search: root.dataset.labelSearch || 'Marke suchen',
      top: root.dataset.labelTop || 'Top-Marken',
      empty: root.dataset.labelEmpty || 'Keine Marke gefunden.',
      categories: root.dataset.labelCategories || 'Kategorien'
    }
    var firstPage
    try {
      firstPage = JSON.parse(dataNode.textContent)
    } catch (error) {
      return
    }

    var fetchPage = function (page) {
      return fetch(root.dataset.jsonUrl + '&page=' + page, { credentials: 'same-origin' }).then(function (response) {
        return response.json()
      })
    }

    loadAllBrands(firstPage, fetchPage)
      .catch(function () {
        return firstPage.brands || []
      })
      .then(function (brands) {
        var state = { query: '', category: '' }
        var rerender = function (keepFocus) {
          render(container, brands, state, labels, rerender)
          if (keepFocus) {
            var search = container.querySelector('.brand-list__search')
            search.focus()
            search.setSelectionRange(search.value.length, search.value.length)
          }
        }
        rerender()
      })
  }

  return {
    OTHER: OTHER,
    initialLetter: initialLetter,
    sortBrands: sortBrands,
    groupByLetter: groupByLetter,
    filterBrands: filterBrands,
    categoriesOf: categoriesOf,
    loadAllBrands: loadAllBrands,
    mount: mount
  }
})
